import {
  FC,
  PropsWithChildren,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { CustomHeaders } from '../../mutator/custom-instance';

export const Resources = [
  'received_invoices',
  'business_document',
  'ap_payee',
  'mf_file',
  'project',
  'report_number_for_expense',
  'dept',
  'ocr_invoice_inference',
] as const;

export type TResources = (typeof Resources)[number];

export type ResourceMap = {
  [key in TResources]: boolean;
};

type TContext = {
  officeMember?: string;
};

const context = createContext<Record<TResources[number], TContext>>({});

type Props = TContext & Partial<ResourceMap>;

export const AgentApplicantOfficeMemberProvider: FC<PropsWithChildren<Props>> =
  memo(({ children, officeMember, ...rest }) => {
    const parent = useContext(context);
    const ctx = parent;
    Object.keys(rest).forEach((resource) => {
      if (officeMember) {
        if (typeGuardResources(resource) && rest[resource] === true) {
          ctx[resource] = { officeMember: officeMember };
        } else {
          delete ctx[resource];
        }
      }
    });
    return <context.Provider value={ctx}>{children}</context.Provider>;
  });
AgentApplicantOfficeMemberProvider.displayName =
  'AgentApplicantOfficeMemberProvider';

export const useAgentApplicantResourceHeaders = (
  resource: TResources
): CustomHeaders => {
  const ctx = useContext(context);
  return useMemo(() => {
    const headers: CustomHeaders = {};
    if (typeGuardResources(resource) && ctx[resource]) {
      headers.officeMemberId = ctx[resource]?.officeMember;
    }
    return headers;
  }, [ctx, resource]);
};

const typeGuardResources = (
  resource: TResources | string | undefined
): resource is TResources => {
  return resource ? Array.from<string>(Resources).includes(resource) : false;
};

export const useAgentApplicantOfficeMemberIdHook = () => {
  const ctx = useContext(context);
  return useCallback(
    (resource: TResources | string | undefined): string | undefined => {
      if (typeGuardResources(resource) && ctx[resource]) {
        return ctx[resource]?.officeMember;
      }
      return undefined;
    },
    [ctx]
  );
};
